import type { TFunction } from 'i18next'

import { messageFor } from '../../lib/formErrors'
import { INSURANCE_COVERAGES } from './api'

/**
 * Which input each document rejection belongs to — the panel's own map over the
 * shared lib/formErrors machinery. One code, one field, as everywhere.
 */
export const FIELD_BY_CODE: Record<string, string> = {
  document_type_invalid: 'type',
  // The type is what does not fit this owner, so the complaint sits on the select.
  document_owner_mismatch: 'type',
  insurance_coverage_needs_certificate: 'type',
  document_expiry_required: 'expiresOn',
  document_dates_inverted: 'expiresOn',
  document_issued_in_future: 'issuedOn',
  document_notes_too_long: 'notes',
  document_file_name_invalid: 'file',
  document_file_type_invalid: 'file',
  document_file_empty: 'file',
  document_file_required: 'file',
  document_file_too_large: 'file',
  // medical_card_needs_cdl_number belongs to no box on this form — the fix is the
  // CDL number on the driver's own form — so it goes to the summary, on purpose.
}

/** Fixing either date can resolve an inverted pair; editing either clears it. */
export const CLEARED_BY: Record<string, readonly string[]> = {
  document_dates_inverted: ['issuedOn', 'expiresOn'],
}

/**
 * The rules one coverage line of a certificate can break, and the cell each one
 * belongs to. The server scopes every such code to its coverage —
 * `insurance_cargo_policy_number_required` — so "one code, one field" still holds
 * on a grid: each line's cell is its own field, named `<coverage>.<cell>`.
 */
const INSURANCE_LINE_CELLS: Record<string, string> = {
  duplicated: 'policyNumber',
  policy_number_required: 'policyNumber',
  policy_number_too_long: 'policyNumber',
  effective_required: 'effectiveOn',
  expiry_required: 'expiresOn',
  dates_inverted: 'expiresOn',
  limit_invalid: 'limit',
  limit_required: 'limit',
}

for (const { key } of INSURANCE_COVERAGES) {
  for (const [rule, cell] of Object.entries(INSURANCE_LINE_CELLS)) {
    FIELD_BY_CODE[`insurance_${key}_${rule}`] = `${key}.${cell}`
  }

  CLEARED_BY[`insurance_${key}_dates_inverted`] = [`${key}.effectiveOn`, `${key}.expiresOn`]
}

/** The coverage and the rule inside a line-scoped code, or null for any other code. */
export function insuranceLineOf(code: string): { key: string; rule: string } | null {
  for (const { key } of INSURANCE_COVERAGES) {
    const prefix = `insurance_${key}_`

    if (code.startsWith(prefix)) {
      return { key, rule: code.slice(prefix.length) }
    }
  }

  return null
}

/**
 * The sentence for one document code, in the active language. A line-scoped code
 * is translated from its rule with the coverage's name filled in — eight sentences
 * cover forty codes — and everything else goes the ordinary way.
 */
export function documentMessageFor(code: string, t: TFunction, fallbackKey: string): string {
  const line = insuranceLineOf(code)

  if (line === null) {
    return messageFor(code, t, fallbackKey)
  }

  const key = `errors.insurance_line.${line.rule}`
  const translated = t(key, { coverage: t(`documents.coverages.${line.key}`) })

  // i18next echoes the key back when it has no translation for it.
  return translated === key ? t(fallbackKey) : translated
}
